<template>
  <v-row class="mt-3 mb-1">
    <v-col cols="12">
      <div
        v-click-outside="closeMenu"
        class="plugin-group-search"
      >
        <v-text-field
          v-model="searchQuery"
          :loading="isSearching"
          :disabled="disabled"
          clearable
          placeholder="Cerca cartelle, documenti e file..."
          prepend-inner-icon="mdi-magnify"
          hide-details
          outlined
          dense
          @input="onSearchInput"
          @focus="openMenu"
          @click:clear="resetSearch"
        />

        <v-card
          v-if="menuOpen && hasResults"
          class="plugin-group-search__results elevation-4"
        >
          <v-list dense>
            <template v-if="folderResults.length">
              <v-subheader class="plugin-group-search__header">
                Cartelle
              </v-subheader>
              <v-list-item
                v-for="item in folderResults"
                :key="item.id"
                @click="navigateTo(item)"
              >
                <v-list-item-avatar
                  tile
                  size="36"
                  color="amber darken-2"
                >
                  <v-icon
                    dark
                    small
                  >
                    mdi-folder
                  </v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ item.title }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.subtitle }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </template>

            <template v-if="documentResults.length">
              <v-subheader class="plugin-group-search__header">
                Documenti
              </v-subheader>
              <v-list-item
                v-for="item in documentResults"
                :key="item.id"
                @click="navigateTo(item)"
              >
                <v-list-item-avatar
                  tile
                  size="36"
                  color="primary"
                >
                  <v-icon
                    dark
                    small
                  >
                    mdi-folder
                  </v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ item.title }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.subtitle }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </template>

            <template v-if="fileResults.length">
              <v-subheader class="plugin-group-search__header">
                File
              </v-subheader>
              <v-list-item
                v-for="item in fileResults"
                :key="item.id"
                @click="navigateTo(item)"
              >
                <v-list-item-avatar
                  tile
                  size="36"
                  :color="item.avatarColor"
                >
                  <v-icon
                    dark
                    small
                  >
                    {{ item.icon }}
                  </v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title>{{ item.title }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.subtitle }}</v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-list>
        </v-card>

        <v-card
          v-else-if="menuOpen && showNoResults"
          class="plugin-group-search__results elevation-4"
        >
          <v-list dense>
            <v-list-item disabled>
              <v-list-item-content>
                <v-list-item-title class="text--secondary">
                  Nessun risultato
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-card>
      </div>
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters } from 'vuex'
import vClickOutside from 'v-click-outside'
import {
  getEntityColorByExtension,
  getMaterialIconByExtension,
} from '@/utils/file-extension'

const MIN_QUERY_LENGTH = 2
const DEBOUNCE_MS = 350

export default {
  name: 'PluginGroupEntitiesSearch',
  directives: {
    clickOutside: vClickOutside.directive,
  },
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    folderRoute: {
      type: String,
      default: 'pluginGroupFolderView',
    },
    documentRoute: {
      type: String,
      default: 'pluginGroupDocumentFiles',
    },
  },
  data() {
    return {
      searchQuery: '',
      folderResults: [],
      documentResults: [],
      fileResults: [],
      menuOpen: false,
      isSearching: false,
      searchDebounceTimer: null,
    }
  },
  computed: {
    ...mapGetters([
      'loggedUserGroup',
      'groupFolders',
      'groupDocumentsList',
      'groupFilesList',
    ]),
    hasResults() {
      return this.folderResults.length > 0
        || this.documentResults.length > 0
        || this.fileResults.length > 0
    },
    showNoResults() {
      return this.searchQuery.trim().length >= MIN_QUERY_LENGTH
        && !this.isSearching
        && !this.hasResults
    },
  },
  beforeDestroy() {
    clearTimeout(this.searchDebounceTimer)
  },
  methods: {
    openMenu() {
      if (this.searchQuery.trim().length >= MIN_QUERY_LENGTH) {
        this.menuOpen = true
      }
    },
    closeMenu() {
      this.menuOpen = false
    },
    resetSearch() {
      this.folderResults = []
      this.documentResults = []
      this.fileResults = []
      this.menuOpen = false
    },
    onSearchInput() {
      clearTimeout(this.searchDebounceTimer)

      const normalizedQuery = this.searchQuery.trim()

      if (normalizedQuery.length < MIN_QUERY_LENGTH) {
        this.resetSearch()

        return
      }

      this.menuOpen = true
      this.isSearching = true
      this.searchDebounceTimer = setTimeout(() => {
        this.runSearch(normalizedQuery)
      }, DEBOUNCE_MS)
    },
    async runSearch(query) {
      try {
        await Promise.all([
          this.$store.dispatch('getGroup'),
          this.$store.dispatch('getGroupDocuments'),
          this.$store.dispatch('getGroupFiles'),
        ])

        this.folderResults = this.buildFolderResults(query)
        this.documentResults = this.buildDocumentResults(query)
        this.fileResults = this.buildFileResults(query)
        this.menuOpen = true
      } catch (error) {
        console.log(error)
        this.folderResults = []
        this.documentResults = []
        this.fileResults = []
      } finally {
        this.isSearching = false
      }
    },
    buildFolderResults(query) {
      const normalizedQuery = query.toLowerCase()

      return (this.groupFolders || [])
        .filter(folder => folder.name?.toLowerCase().includes(normalizedQuery))
        .map(folder => ({
          id: `folder-${folder.id}`,
          type: 'folder',
          title: folder.name,
          subtitle: this.buildFolderPath(folder.id),
          route: {
            name: this.folderRoute,
            params: {
              id: folder.group_id,
              fid: folder.unique_ref,
            },
          },
        }))
        .sort((a, b) => a.title.localeCompare(b.title, 'it'))
    },
    buildDocumentResults(query) {
      const normalizedQuery = query.toLowerCase()

      return (this.groupDocumentsList || [])
        .filter(document => document.title?.toLowerCase().includes(normalizedQuery))
        .map(document => ({
          id: `document-${document.id}`,
          type: 'document',
          title: document.title,
          subtitle: document.folder_id
            ? this.buildFolderPath(document.folder_id)
            : 'Spazio condiviso',
          route: {
            name: this.documentRoute,
            params: { did: document.id },
          },
        }))
        .sort((a, b) => a.title.localeCompare(b.title, 'it'))
    },
    buildFileResults(query) {
      const normalizedQuery = query.toLowerCase()
      const results = []

      ;(this.groupDocumentsList || []).forEach(document => {
        ;(document.files || []).forEach(file => {
          if (!file.original_name?.toLowerCase().includes(normalizedQuery)) {
            return
          }

          results.push({
            id: `doc-file-${document.id}-${file.id}`,
            type: 'file',
            title: file.original_name,
            subtitle: this.buildDocumentFileSubtitle(document),
            icon: getMaterialIconByExtension(file.extension, file.original_name),
            avatarColor: getEntityColorByExtension(file.extension, file.original_name),
            route: {
              name: this.documentRoute,
              params: { did: document.id },
            },
          })
        })
      })

      ;(this.groupFilesList || []).forEach(file => {
        if (!file.original_name?.toLowerCase().includes(normalizedQuery)) {
          return
        }

        results.push({
          id: `group-file-${file.id}`,
          type: 'file',
          title: file.original_name,
          subtitle: this.buildGroupFileSubtitle(file.group_folder_id),
          icon: getMaterialIconByExtension(file.extension, file.original_name),
          avatarColor: getEntityColorByExtension(file.extension, file.original_name),
          route: this.buildGroupFileRoute(file.group_folder_id),
        })
      })

      return results.sort((a, b) => a.title.localeCompare(b.title, 'it'))
    },
    buildFolderPath(folderId) {
      if (!folderId) {
        return 'Spazio condiviso'
      }

      const parts = []
      let current = (this.groupFolders || []).find(item => item.id === folderId)

      while (current) {
        parts.unshift(current.name)
        if (!current.parent_id) {
          break
        }
        current = (this.groupFolders || []).find(item => item.id === current.parent_id)
      }

      return parts.length
        ? `Spazio condiviso > ${parts.join(' > ')}`
        : 'Spazio condiviso'
    },
    buildDocumentFileSubtitle(document) {
      const location = document.folder_id
        ? this.buildFolderPath(document.folder_id)
        : 'Spazio condiviso'

      return `${location} · Documento: ${document.title}`
    },
    buildGroupFileSubtitle(folderId) {
      if (!folderId) {
        return 'Spazio condiviso'
      }

      return this.buildFolderPath(folderId)
    },
    buildGroupFileRoute(folderId) {
      if (!folderId) {
        return { name: 'pluginGroupDocuments' }
      }

      const folder = (this.groupFolders || []).find(item => item.id === folderId)

      if (!folder) {
        return { name: 'pluginGroupDocuments' }
      }

      return {
        name: this.folderRoute,
        params: {
          id: folder.group_id,
          fid: folder.unique_ref,
        },
      }
    },
    navigateTo(item) {
      if (!item?.route) {
        return
      }

      this.$router.push(item.route)
      this.searchQuery = ''
      this.resetSearch()
    },
  },
}
</script>

<style scoped>
.plugin-group-search {
  position: relative;
}

.plugin-group-search__results {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  z-index: 6;
  max-height: 360px;
  overflow-y: auto;
}

.plugin-group-search__header {
  height: 36px;
  font-weight: 600;
}
</style>
